import { useEffect, useRef, useState, useCallback } from 'react';
import TigerSQLite from '@shared/utils/mobile/tigerSqlite';
import postFile from '@shared/utils/transactional/postFile';
import useConnection from '@shared/hooks/useConnetions/useConnection';
import useToast from '@shared/hooks/useToast';
import { useTiger } from '@shared/context/mobile/useTiger';
import { ESTADOS_FINALES_LIST } from '@conductor/constants/estadosOperacion';

const TABLA = 'cola_offline';
const TABLA_IMGS = 'cola_offline_imgs';

// Backoff a nivel de módulo (no useRef): así sobrevive al remontaje del hook al navegar
// entre pantallas; si no, una cola atascada reiniciaría el backoff y martillaría /estado y /s3.
const backoffStore = {};

// Guard de procesamiento a nivel de MÓDULO (no useRef): garantiza que solo UNA ejecución de
// procesarCola corra a la vez, aunque haya varias instancias del hook montadas (ej. el drenador
// global del layout + PageMapChofer). Evita el doble envío de la misma operación.
let procesando = false;

const CAMPOS = [
    { name: 'id',           type: 'INTEGER PRIMARY KEY' },
    { name: 'operacion_id', type: 'TEXT' },
    { name: 'pasos',        type: 'TEXT' },
    { name: 'created_at',   type: 'TEXT' },
];

const CAMPOS_IMGS = [
    { name: 'img_key',  type: 'TEXT PRIMARY KEY' },
    { name: 'img_data', type: 'TEXT' },
];

export default function useOfflineQueue() {
    const [cantidadPendiente, setCantidadPendiente] = useState(0);

    const connection = useConnection();
    const { newToast } = useToast();
    const tiger = useTiger();
    const isOnline = tiger.isOnline.value;

    // Init: crear tablas si no existen
    useEffect(() => {
        Promise.all([
            TigerSQLite.fetch(TABLA, 'tableExists').then((existe) => {
                if (!existe) return TigerSQLite.modifyTable('create', TABLA, CAMPOS);
            }),
            TigerSQLite.modifyTable('create', TABLA_IMGS, CAMPOS_IMGS).catch(() => {}),
        ])
            .then(() => actualizarContador())
            .catch((e) => console.warn('[OfflineQueue] Error init tablas:', e));
    }, []);

    async function actualizarContador() {
        try {
            const total = await TigerSQLite.fetch(TABLA, 'count');
            setCantidadPendiente(Number(total) || 0);
        } catch {
            setCantidadPendiente(0);
        }
    }

    // Convierte la respuesta del backend en un Error clasificado, o null si fue OK.
    // - reintentable = true  → falla transitoria (red, sin respuesta, 5xx). Se reintenta.
    // - reintentable = false → rechazo de negocio (4xx). NO se reintenta: no se arregla solo.
    function clasificarRespuesta(response) {
        if (!response) {
            const e = new Error('Sin respuesta del servidor');
            e.reintentable = true;
            return e;
        }
        if (!response.estado) {
            const status = response.networkInfo?.status;
            const e = new Error(response.message || 'Error del servidor');
            e.status = status;
            // 4xx = rechazo de negocio (no reintentable) SALVO los transitorios:
            // 401/403 (token), 408 (timeout), 409 (conflicto/evidencia aún no drenó),
            // 425 (too early), 429 (rate limit). 5xx/sin status también reintentable.
            const TRANSITORIOS_4XX = [401, 403, 408, 409, 425, 429];
            const negocio4xx = status >= 400 && status < 500 && !TRANSITORIOS_4XX.includes(status);
            e.reintentable = !negocio4xx;
            return e;
        }
        return null;
    }

    // Una excepción se reintenta salvo que esté marcada explícitamente como no-reintentable
    // (rechazo de negocio 4xx). Las fallas de red crudas de fetch no llevan flag → reintentables.
    const esReintentable = (e) => e?.reintentable !== false;

    // Callbacks opcionales para idempotencia en reintentos:
    //  - onImagenSubida(i, url): tras subir con éxito el archivo de un paso (para persistir
    //    la URL en la cola y NO re-subir si un paso posterior falla).
    //  - onPasoOk(i): tras completar un paso (para quitarlo de la cola).
    async function ejecutarPasos(pasos, { onPasoOk, onImagenSubida } = {}) {
        for (let i = 0; i < pasos.length; i++) {
            const paso = pasos[i];
            let payload = { ...paso.payload };

            // Si en un intento anterior ya se subió el archivo, reusar la URL (no re-subir).
            if (!paso.imagen_base64 && paso.payload?.archivo_url) {
                payload.archivo_url = paso.payload.archivo_url;
            }

            if (paso.imagen_base64) {
                console.log(`[FOTO] Paso ${i + 1}: subiendo imagen (${(paso.imagen_base64.length / 1024).toFixed(1)}KB) a ${paso.imagen_location}`);
                const responseImagen = await postFile({
                    name: 'archivo.png',
                    fileBase64: paso.imagen_base64,
                    location: paso.imagen_location || 'evidencias',
                });
                console.log(`[FOTO] Respuesta postFile:`, JSON.stringify(responseImagen));
                if (!responseImagen.estado) {
                    // Falló la subida del archivo al servicio transactional (/s3/private).
                    // No es pérdida de conexión: mostramos el error real. Un 4xx (auth/validación)
                    // no se reintenta (loopearía); 5xx/sin status sí (transitorio).
                    const st = responseImagen.status;
                    const e = new Error(responseImagen.message || 'No se pudo subir la imagen/evidencia');
                    e.status = st;
                    e.subidaFallida = true;
                    e.reintentable = !(st >= 400 && st < 500);
                    throw e;
                }
                payload.archivo_url = responseImagen.url;
                if (onImagenSubida) await onImagenSubida(i, responseImagen.url);
            }

            if (paso.url === '__upload_incidencia_adjunto__') {
                if (payload.incidencia_viajes && payload.archivo_url) {
                    payload.incidencia_viajes.adjunto = payload.archivo_url;
                }
                const response = await connection({ url: '/incidencia_viajes/guardar', body: payload });
                const err = clasificarRespuesta(response);
                if (err) throw err;
                if (onPasoOk) await onPasoOk(i);
                continue;
            }

            // Paso genérico (no ligado a una operación/evidencia): soporta method
            // opcional (p.ej. PUT /entregas/entregaEstado). Sin `method` → POST,
            // que es como funcionaban TODOS los pasos de evidencia → no se altera ese flujo.
            const response = await connection({ url: paso.url, method: paso.method || 'POST', body: payload });
            const err = clasificarRespuesta(response);
            if (err) throw err;
            if (onPasoOk) await onPasoOk(i);
        }
    }

    const ESTADOS_FINALES = ESTADOS_FINALES_LIST;

    async function yaFueProcesada(pasos) {
        try {
            const pasoFinalizar = pasos.find(p => p.url === '/operaciones/finalizar');
            if (!pasoFinalizar?.payload?.operacionId) return false;

            const res = await connection({
                url: `/operaciones/${pasoFinalizar.payload.operacionId}/estado`,
                method: 'GET',
            });

            if (res?.response?.estadoOperacion && ESTADOS_FINALES.includes(res.response.estadoOperacion)) {
                return true;
            }
        } catch (e) {
            console.warn('[OfflineQueue] Error consultando estado:', e?.message);
        }
        return false;
    }

    // Re-hidratar imágenes desde SQLite (tabla cola_offline_imgs)
    async function rehidratarImagenes(pasos) {
        const result = [];
        for (const p of pasos) {
            if (!p._imgKey) { result.push(p); continue; }
            let img = null;
            try {
                const rows = await TigerSQLite.fetch(TABLA_IMGS, 'findByParam', { img_key: p._imgKey });
                console.log(`[FOTO][rehidratar] key=${p._imgKey}, rows encontrados=${rows?.length || 0}`);
                img = rows?.[0]?.img_data || null;
            } catch (e) {
                console.warn(`[FOTO][rehidratar] Error SQLite para ${p._imgKey}:`, e?.message);
            }

            // Fallback: localStorage (registros viejos)
            if (!img) {
                img = localStorage.getItem(p._imgKey);
                console.log(`[FOTO][rehidratar] Fallback localStorage key=${p._imgKey}, encontrado=${!!img}`);
            }

            if (img) {
                console.log(`[FOTO][rehidratar] OK: ${p._imgKey} (${(img.length / 1024).toFixed(1)}KB)`);
            } else {
                console.error(`[FOTO][rehidratar] PERDIDA: ${p._imgKey} — no está en SQLite ni localStorage`);
            }
            result.push({ ...p, imagen_base64: img });
        }
        return result;
    }

    async function limpiarImagenes(pasos) {
        for (const p of pasos) {
            if (p._imgKey) {
                try { await TigerSQLite.deleteRecord(TABLA_IMGS, [{ whereClause: 'img_key = ?', whereArgs: [p._imgKey] }]); } catch {}
                try { localStorage.removeItem(p._imgKey); } catch {}
            }
        }
    }

    // Tras 24h sin poder enviarse, un registro se descarta aunque la falla sea "transitoria"
    // (evita reintentos infinitos silenciosos si el backend nunca acepta el registro).
    const MAX_AGE_MS = 24 * 60 * 60 * 1000;
    function esVencido(registro) {
        const t = new Date(registro.created_at).getTime();
        if (isNaN(t)) return false;
        return (Date.now() - t) > MAX_AGE_MS;
    }

    async function borrarRegistro(id) {
        await TigerSQLite.deleteRecord(TABLA, [
            { whereClause: 'id = ?', whereArgs: [String(id)] }
        ]);
    }

    async function procesarRegistro(registro) {
        const pasosRaw = JSON.parse(registro.pasos);

        if (await yaFueProcesada(pasosRaw)) {
            await limpiarImagenes(pasosRaw);
            await borrarRegistro(registro.id);
            return { tipo: 'omitido' };
        }

        const pasos = await rehidratarImagenes(pasosRaw);

        try {
            await ejecutarPasos(pasos, {
                onImagenSubida: async (idx, url) => {
                    // La imagen ya está en el server: fijamos la URL en el paso y borramos el
                    // base64, para que un reintento (si falla el POST siguiente) NO la re-suba.
                    const imgKey = pasosRaw[idx]?._imgKey;
                    pasosRaw[idx] = {
                        ...pasosRaw[idx],
                        _imgKey: undefined,
                        imagen_base64: null,
                        payload: { ...pasosRaw[idx].payload, archivo_url: url },
                    };
                    if (imgKey) {
                        try { await TigerSQLite.deleteRecord(TABLA_IMGS, [{ whereClause: 'img_key = ?', whereArgs: [imgKey] }]); } catch {}
                        try { localStorage.removeItem(imgKey); } catch {}
                    }
                    // Persistir desde idx en adelante (este paso ya con la URL).
                    await TigerSQLite.update(
                        TABLA, { pasos: JSON.stringify(pasosRaw.slice(idx)) }, 'id = ?', [String(registro.id)]
                    ).catch(() => {});
                },
                onPasoOk: async (idx) => {
                    // Idempotencia: al completar un paso lo quitamos de la cola para que un
                    // reintento posterior no lo reenvíe (evita evidencias FIRMA/FOTO duplicadas).
                    await limpiarImagenes([pasosRaw[idx]]);
                    const restantes = pasosRaw.slice(idx + 1);
                    if (restantes.length) {
                        await TigerSQLite.update(
                            TABLA, { pasos: JSON.stringify(restantes) }, 'id = ?', [String(registro.id)]
                        ).catch(() => {});
                    }
                },
            });
        } catch (e) {
            if (esReintentable(e) && !esVencido(registro)) {
                // Falla transitoria: el registro queda en la cola para el próximo intento.
                const err = new Error(e.message);
                err.tipo = 'reintentar';
                throw err;
            }
            // Rechazo de negocio (4xx) o registro vencido: se descarta para no spamear.
            await limpiarImagenes(pasosRaw);
            await borrarRegistro(registro.id);
            const err = new Error(e.message);
            err.tipo = 'descartado';
            throw err;
        }

        await limpiarImagenes(pasosRaw);
        await borrarRegistro(registro.id);
        return { tipo: 'enviado' };
    }

    const procesarCola = useCallback(async () => {
        if (procesando) return 0;
        procesando = true;

        try {
            let registros;
            try {
                registros = await TigerSQLite.fetch(TABLA, 'fetchAll');
            } catch (fetchErr) {
                const errMsg = fetchErr?.error || fetchErr?.message || String(fetchErr);

                if (errMsg.includes('CursorWindow') || errMsg.includes('Row too big')) {
                    await TigerSQLite.modifyTable('delete', TABLA).catch(() => {});
                    await TigerSQLite.modifyTable('create', TABLA, CAMPOS).catch(() => {});
                    await actualizarContador();
                    newToast('Operaciones pendientes no pudieron recuperarse — guardá de nuevo', 'warning');
                    procesando = false;
                    return 0;
                }

                try {
                    await TigerSQLite.modifyTable('create', TABLA, CAMPOS);
                    registros = await TigerSQLite.fetch(TABLA, 'fetchAll');
                } catch {
                    procesando = false;
                    return 0;
                }
            }

            if (!registros || registros.length === 0) {
                procesando = false;
                return 0;
            }

            let enviados = 0;
            let omitidos = 0;
            let reintentar = 0;
            const descartados = [];

            for (const registro of registros) {
                // Si esta operación falló hace poco por algo transitorio, no la reintentamos
                // todavía (corta las ráfagas de /estado y /s3 cada 15s con una cola atascada).
                const bo = backoffStore[registro.id];
                if (bo && bo.hasta > Date.now()) continue;

                try {
                    const resultado = await procesarRegistro(registro);
                    delete backoffStore[registro.id];
                    if (resultado.tipo === 'omitido') omitidos++;
                    else enviados++;
                } catch (e) {
                    if (e?.tipo === 'reintentar') {
                        // Falla transitoria: backoff creciente (30s, 1m, 2m, 4m… máx 5m).
                        const fallos = (bo?.fallos || 0) + 1;
                        const espera = Math.min(30000 * Math.pow(2, fallos - 1), 5 * 60 * 1000);
                        backoffStore[registro.id] = { hasta: Date.now() + espera, fallos };
                        reintentar++;
                        console.warn(`[OfflineQueue] Reintentará en ${Math.round(espera / 1000)}s:`, e?.message);
                    } else {
                        // Descartada (rechazo de negocio o vencida): se avisa el motivo real una vez.
                        delete backoffStore[registro.id];
                        console.warn(`[OfflineQueue] Operación descartada (op=${registro.operacion_id}, registro=${registro.id}):`, e?.message);
                        descartados.push(e?.message || 'Operación rechazada por el servidor');
                    }
                }
            }

            await actualizarContador();

            if (enviados > 0) {
                newToast(`${enviados} operación${enviados > 1 ? 'es' : ''} enviada${enviados > 1 ? 's' : ''} correctamente`);
            }
            if (descartados.length) {
                const msg = descartados.length === 1
                    ? descartados[0]
                    : `${descartados.length} operaciones no pudieron completarse`;
                newToast(msg, 'error');
            }
            // reintentar → sin toast: se reenvía solo al recuperar señal

            // Devuelve cuántas operaciones cambiaron de estado en el server (enviadas o ya
            // procesadas). La pantalla solo refresca si esto es > 0 → evita refetch inútiles.
            return enviados + omitidos;
        } catch (e) {
            console.warn('[OfflineQueue] Error general:', e?.message);
            return 0;
        } finally {
            procesando = false;
        }
    }, [connection, newToast]);

    // Guardar en SQLite para enviar después
    // Imágenes en tabla separada (cola_offline_imgs) para evitar CursorWindow
    async function guardarEnCola(operacionId, pasos) {
        const ts = Date.now();
        await TigerSQLite.modifyTable('create', TABLA_IMGS, CAMPOS_IMGS).catch(() => {});

        const pasosLivianos = [];
        for (let i = 0; i < pasos.length; i++) {
            const p = pasos[i];
            if (!p.imagen_base64) { pasosLivianos.push(p); continue; }

            const imgKey = `offline_img_${operacionId}_${i}_${ts}`;
            const imgSize = (p.imagen_base64.length / 1024).toFixed(1);
            console.log(`[FOTO][guardar] Guardando imagen key=${imgKey}, size=${imgSize}KB`);

            try {
                await TigerSQLite.insert(TABLA_IMGS, { img_key: imgKey, img_data: p.imagen_base64 });
                console.log(`[FOTO][guardar] INSERT OK key=${imgKey}`);

                // Verificar que se guardó
                const check = await TigerSQLite.fetch(TABLA_IMGS, 'findByParam', { img_key: imgKey });
                const saved = check?.[0]?.img_data;
                if (saved) {
                    console.log(`[FOTO][guardar] VERIFICADO OK key=${imgKey}, size guardado=${(saved.length / 1024).toFixed(1)}KB`);
                } else {
                    console.error(`[FOTO][guardar] VERIFICACIÓN FALLÓ — key=${imgKey} no se encuentra después de insert`);
                }
            } catch (e) {
                console.error(`[FOTO][guardar] ERROR INSERT key=${imgKey}:`, e?.message);
            }
            pasosLivianos.push({ ...p, imagen_base64: null, _imgKey: imgKey });
        }

        await TigerSQLite.modifyTable('create', TABLA, CAMPOS).catch(() => {});
        await TigerSQLite.insert(TABLA, {
            operacion_id: String(operacionId),
            pasos: JSON.stringify(pasosLivianos),
            created_at: new Date().toISOString(),
        });
        await actualizarContador();
    }

    const encolar = useCallback(async (operacionId, pasos) => {
        if (isOnline && navigator.onLine) {
            try {
                await ejecutarPasos(pasos);
                return { estado: true, offline: false };
            } catch (e) {
                // 1. Rechazo de negocio del backend (4xx): mostrar el motivo, no encolar.
                if (e.status >= 400 && e.status < 500) {
                    console.warn('[OfflineQueue] Rechazo del servidor (4xx):', e?.message);
                    newToast(e.message || 'No se pudo completar la operación', 'error');
                    return { estado: false, offline: false };
                }
                // 2. HAY conexión pero el servidor respondió un error (5xx) o falló la subida
                //    del archivo. NO es pérdida de conexión: mostramos el error REAL en vez de
                //    "se perdió la conexión", y no encolamos un fallo que se repetiría siempre.
                if (navigator.onLine && (e.status || e.subidaFallida)) {
                    console.warn('[OfflineQueue] Error online (no es falta de conexión):', e?.status, e?.message);
                    newToast(e.message || 'No se pudo completar la operación. Intentá de nuevo.', 'error');
                    return { estado: false, offline: false };
                }
                // 3. Pérdida real de conexión (sin respuesta / request cortado): encolar.
                console.warn('[OfflineQueue] Conexión perdida, encolando:', e?.message);
                try {
                    await guardarEnCola(operacionId, pasos);
                    newToast('Se perdió la conexión — se enviará al recuperar señal', 'warning');
                    return { estado: true, offline: true };
                } catch (e2) {
                    console.error('[OfflineQueue] Error al encolar fallback:', e2?.message);
                    newToast('Error al guardar operación', 'error');
                    return { estado: false, offline: true };
                }
            }
        }

        try {
            await guardarEnCola(operacionId, pasos);
            newToast('Sin conexión — se enviará al recuperar señal', 'warning');
            return { estado: true, offline: true };
        } catch (e) {
            console.error('[OfflineQueue] Error al encolar:', e?.message);
            newToast('Error al guardar operación offline', 'error');
            return { estado: false, offline: true };
        }
    }, [isOnline, connection, newToast]);

    return { encolar, cantidadPendiente, procesarCola };
}
